import React, { useState, useEffect } from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // Update active section based on scroll position
      const sections = document.querySelectorAll('[data-section]');
      let current = '';
      
      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.clientHeight;
        if (window.pageYOffset >= sectionTop - 200) {
          current = section.getAttribute('data-section');
        }
      });
      
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(`[data-section="${sectionId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const tableOfContents = [
    { id: 'overview', title: 'Overview' },
    { id: 'information-collection', title: 'Information We Collect' },
    { id: 'data-usage', title: 'How We Use Your Data' },
    { id: 'ai-processing', title: 'AI & Data Processing' },
    { id: 'data-sharing', title: 'Data Sharing' },
    { id: 'security', title: 'Data Security' },
    { id: 'your-rights', title: 'Your Rights' },
    { id: 'retention', title: 'Data Retention' },
    { id: 'international', title: 'International Transfers' },
    { id: 'updates', title: 'Policy Updates' },
    { id: 'contact', title: 'Contact Us' }
  ];

  return (
    <div className="privacy-policy">
      {/* Header */}
      <header className={`policy-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <div className="breadcrumb">
              <span>Legal</span>
              <span className="separator">/</span>
              <span>Privacy Policy</span>
            </div>
            <h1>Privacy Policy</h1>
            <p className="header-description">
              Learn how NyLeads AI collects, uses, and protects your information
            </p>
            <div className="last-updated">
              <span className="update-badge">Last updated</span>
              <time dateTime="2026-01-28">January 28, 2025</time>
            </div>
          </div>
        </div>
      </header>

      <div className="policy-container">
        <div className="container">
          <div className="policy-layout">
            {/* Table of Contents */}
            <aside className="toc-sidebar">
              <div className="toc-sticky">
                <h3>Contents</h3>
                <nav className="toc-nav">
                  {tableOfContents.map((item) => (
                    <button
                      key={item.id}
                      className={`toc-link ${activeSection === item.id ? 'active' : ''}`}
                      onClick={() => scrollToSection(item.id)}
                    >
                      {item.title}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="policy-content">
              {/* Overview */}
              <section data-section="overview" className="policy-section">
                <h2>Overview</h2>
                <p className="lead">
                  At NyLeads AI, we take your privacy seriously. This Privacy Policy explains how we collect,
                  use, disclose, and safeguard your information when you use our AI-powered platform
                  designed to provide verified restaurant leads and business intelligence across New York City.
                </p>
                <div className="info-card">
                  <div className="info-card-header">
                    <h4>Key Points</h4>
                  </div>
                  <ul>
                    <li>We only collect data necessary to provide our restaurant lead generation services</li>
                    <li>Your business data is processed securely and never shared with competitors</li>
                    <li>You maintain full control over your data with easy deletion options</li>
                    <li>We use enterprise-grade security to protect your information</li>
                  </ul>
                </div>
              </section>

              {/* Information Collection */}
              <section data-section="information-collection" className="policy-section">
                <h2>Information We Collect</h2>
                
                <h3>Personal Information</h3>
                <p>
                  We collect personal information that you provide directly to us when creating an account
                  or using our services:
                </p>
                <div className="data-grid">
                  <div className="data-item">
                    <h4>Account Data</h4>
                    <ul>
                      <li>Name and email address</li>
                      <li>Account preferences</li>
                      <li>Profile information</li>
                    </ul>
                  </div>
                  <div className="data-item">
                    <h4>Business Information</h4>
                    <ul>
                      <li>Company name and type</li>
                      <li>Target restaurant categories</li>
                      <li>Sales territory preferences</li>
                    </ul>
                  </div>
                  <div className="data-item">
                    <h4>Payment Information</h4>
                    <ul>
                      <li>Billing details</li>
                      <li>Payment history</li>
                      <li>Subscription status</li>
                    </ul>
                  </div>
                </div>

                <h3>Automatically Collected Information</h3>
                <p>
                  We automatically collect certain information when you use our services:
                </p>
                <ul>
                  <li><strong>Usage Data:</strong> How you interact with our platform and lead search features</li>
                  <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
                  <li><strong>Performance Data:</strong> Error logs, response times, feature usage</li>
                  <li><strong>Analytics:</strong> Anonymized usage patterns and trends</li>
                </ul>
              </section>

              {/* Data Usage */}
              <section data-section="data-usage" className="policy-section">
                <h2>How We Use Your Data</h2>
                <p>
                  We use the information we collect for specific, legitimate business purposes:
                </p>

                <div className="usage-grid">
                  <div className="usage-card primary">
                    <div className="usage-icon">🍽️</div>
                    <h4>Lead Generation</h4>
                    <p>Deliver AI-curated New York restaurant leads matched to your target criteria and sales goals</p>
                  </div>
                  <div className="usage-card">
                    <div className="usage-icon">⚡</div>
                    <h4>Service Delivery</h4>
                    <p>Provide, maintain, and improve our lead discovery platform and data tools</p>
                  </div>
                  <div className="usage-card">
                    <div className="usage-icon">🔒</div>
                    <h4>Security & Safety</h4>
                    <p>Detect fraud, abuse, and security threats to protect our users</p>
                  </div>
                  <div className="usage-card">
                    <div className="usage-icon">📊</div>
                    <h4>Analytics</h4>
                    <p>Understand usage patterns to enhance our algorithms and lead accuracy</p>
                  </div>
                  <div className="usage-card">
                    <div className="usage-icon">💬</div>
                    <h4>Communication</h4>
                    <p>Send important updates, support responses, and service notifications</p>
                  </div>
                  <div className="usage-card">
                    <div className="usage-icon">⚖️</div>
                    <h4>Legal Compliance</h4>
                    <p>Meet regulatory requirements and respond to legal obligations</p>
                  </div>
                </div>
              </section>

              {/* AI Processing */}
              <section data-section="ai-processing" className="policy-section">
                <h2>AI & Data Processing</h2>
                <p>
                  Our AI-powered lead generation engine processes publicly available restaurant data
                  and your preferences to surface the most relevant NYC restaurant leads for your business.
                </p>
                
                <div className="ai-process-flow">
                  <div className="process-step">
                    <div className="step-number">1</div>
                    <h4>Data Aggregation</h4>
                    <p>We aggregate publicly available NYC restaurant data including listings, contacts, and attributes</p>
                  </div>
                  <div className="process-arrow">→</div>
                  <div className="process-step">
                    <div className="step-number">2</div>
                    <h4>AI Matching</h4>
                    <p>Our algorithms match and score restaurants based on your ideal customer profile</p>
                  </div>
                  <div className="process-arrow">→</div>
                  <div className="process-step">
                    <div className="step-number">3</div>
                    <h4>Lead Delivery</h4>
                    <p>You receive verified, actionable restaurant leads tailored to your sales territory</p>
                  </div>
                </div>

                <div className="security-notice">
                  <h4>🔐 AI Security Commitment</h4>
                  <ul>
                    <li>All processing occurs on secure, encrypted servers</li>
                    <li>Your preferences are never used to train models for other users</li>
                    <li>AI processing is anonymized and aggregated when possible</li>
                    <li>We maintain strict access controls for AI systems</li>
                  </ul>
                </div>
              </section>

              {/* Data Sharing */}
              <section data-section="data-sharing" className="policy-section">
                <h2>Data Sharing and Disclosure</h2>
                <p>
                  We do not sell, rent, or trade your personal information. We only share data in
                  limited circumstances:
                </p>

                <div className="sharing-grid">
                  <div className="sharing-card allowed">
                    <h4>✅ Service Providers</h4>
                    <p>Trusted partners who help us deliver our services:</p>
                    <ul>
                      <li>Payment processing providers</li>
                      <li>Cloud infrastructure providers</li>
                      <li>Analytics and monitoring services</li>
                      <li>Customer support platforms</li>
                    </ul>
                  </div>
                  <div className="sharing-card restricted">
                    <h4>⚖️ Legal Requirements</h4>
                    <p>When required by law or to protect rights:</p>
                    <ul>
                      <li>Valid legal processes and court orders</li>
                      <li>Government agency requests</li>
                      <li>Protection of our legal rights</li>
                      <li>Prevention of fraud or abuse</li>
                    </ul>
                  </div>
                </div>

                <div className="no-sharing-notice">
                  <h4>🚫 We Never Share</h4>
                  <ul>
                    <li>Your data with competitors or third-party marketers</li>
                    <li>Individual user data for advertising purposes</li>
                    <li>Your lead search criteria or business strategy</li>
                    <li>Personal information for profit or commercial gain</li>
                  </ul>
                </div>
              </section>

              {/* Security */}
              <section data-section="security" className="policy-section">
                <h2>Data Security</h2>
                <p>
                  We implement comprehensive security measures to protect your information from
                  unauthorized access, disclosure, alteration, and destruction.
                </p>

                <div className="security-measures">
                  <div className="security-category">
                    <h4>🔐 Encryption</h4>
                    <ul>
                      <li>TLS 1.3 encryption for all data transmission</li>
                      <li>AES-256 encryption for stored data</li>
                      <li>End-to-end encryption for sensitive operations</li>
                    </ul>
                  </div>
                  <div className="security-category">
                    <h4>🏰 Infrastructure</h4>
                    <ul>
                      <li>SOC 2 Type II compliant data centers</li>
                      <li>Regular security audits and penetration testing</li>
                      <li>Multi-factor authentication requirements</li>
                    </ul>
                  </div>
                  <div className="security-category">
                    <h4>👥 Access Controls</h4>
                    <ul>
                      <li>Principle of least privilege access</li>
                      <li>Regular access reviews and updates</li>
                      <li>Employee security training and background checks</li>
                    </ul>
                  </div>
                  <div className="security-category">
                    <h4>📊 Monitoring</h4>
                    <ul>
                      <li>24/7 security monitoring and alerts</li>
                      <li>Automated threat detection systems</li>
                      <li>Incident response and recovery procedures</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Your Rights */}
              <section data-section="your-rights" className="policy-section">
                <h2>Your Rights and Choices</h2>
                <p>
                  You have important rights regarding your personal information. Here's how you can
                  exercise them:
                </p>

                <div className="rights-grid">
                  <div className="right-card">
                    <div className="right-icon">👁️</div>
                    <h4>Right to Access</h4>
                    <p>Request a copy of all personal data we hold about you</p>
                    <button className="action-btn">Request Data Export</button>
                  </div>
                  <div className="right-card">
                    <div className="right-icon">✏️</div>
                    <h4>Right to Rectification</h4>
                    <p>Correct or update inaccurate personal information</p>
                    <button className="action-btn">Update Information</button>
                  </div>
                  <div className="right-card">
                    <div className="right-icon">🗑️</div>
                    <h4>Right to Erasure</h4>
                    <p>Request deletion of your personal data</p>
                    <button className="action-btn destructive">Delete Account</button>
                  </div>
                  <div className="right-card">
                    <div className="right-icon">📦</div>
                    <h4>Right to Portability</h4>
                    <p>Receive your data in a machine-readable format</p>
                    <button className="action-btn">Export Data</button>
                  </div>
                  <div className="right-card">
                    <div className="right-icon">🚫</div>
                    <h4>Right to Object</h4>
                    <p>Opt out of certain data processing activities</p>
                    <button className="action-btn">Manage Preferences</button>
                  </div>
                  <div className="right-card">
                    <div className="right-icon">⏸️</div>
                    <h4>Right to Restrict</h4>
                    <p>Limit how we process your information</p>
                    <button className="action-btn">Set Restrictions</button>
                  </div>
                </div>

                <div className="rights-notice">
                  <p>
                    <strong>How to Exercise Your Rights:</strong> Contact us at privacy@nyleads.ai
                    or use the buttons above. We'll respond within 30 days and verify your identity
                    before processing requests.
                  </p>
                </div>
              </section>

              {/* Data Retention */}
              <section data-section="retention" className="policy-section">
                <h2>Data Retention</h2>
                <p>
                  We retain your information only as long as necessary to provide our services and
                  meet legal obligations:
                </p>

                <div className="retention-timeline">
                  <div className="timeline-item">
                    <div className="timeline-marker active"></div>
                    <h4>Active Account</h4>
                    <p>While your account is active and you use our services</p>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <h4>Account Deletion</h4>
                    <p>30 days after account deletion (for recovery purposes)</p>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <h4>Lead & Search Data</h4>
                    <p>1 year after service termination (for performance analysis)</p>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <h4>Legal Records</h4>
                    <p>As required by applicable laws (typically 7 years for financial records)</p>
                  </div>
                </div>
              </section>

              {/* International Transfers */}
              <section data-section="international" className="policy-section">
                <h2>International Data Transfers</h2>
                <p>
                  Your information may be transferred to and processed in countries other than your
                  country of residence. We ensure appropriate safeguards are in place:
                </p>

                <div className="transfer-safeguards">
                  <div className="safeguard-item">
                    <h4>🌍 Adequacy Decisions</h4>
                    <p>We transfer data to countries with adequacy decisions from relevant authorities</p>
                  </div>
                  <div className="safeguard-item">
                    <h4>📋 Standard Contractual Clauses</h4>
                    <p>We use SCCs approved by regulatory authorities for international transfers</p>
                  </div>
                  <div className="safeguard-item">
                    <h4>🔒 Additional Safeguards</h4>
                    <p>Encryption, access controls, and regular compliance assessments</p>
                  </div>
                </div>
              </section>

              {/* Policy Updates */}
              <section data-section="updates" className="policy-section">
                <h2>Policy Updates</h2>
                <p>
                  We may update this Privacy Policy to reflect changes in our practices, services,
                  or legal requirements. We'll notify you of material changes through:
                </p>

                <div className="notification-methods">
                  <div className="notification-item">
                    <div className="notification-icon">📧</div>
                    <h4>Email Notification</h4>
                    <p>Direct email to your registered address for significant changes</p>
                  </div>
                  <div className="notification-item">
                    <div className="notification-icon">🔔</div>
                    <h4>In-App Alerts</h4>
                    <p>Notifications within our platform dashboard</p>
                  </div>
                  <div className="notification-item">
                    <div className="notification-icon">🌐</div>
                    <h4>Website Banner</h4>
                    <p>Prominent notice on our website and legal pages</p>
                  </div>
                </div>

                <div className="update-notice">
                  <p>
                    <strong>Your Continued Use:</strong> By continuing to use our services after
                    policy updates, you accept the revised terms. If you disagree with changes,
                    you can discontinue use or delete your account.
                  </p>
                </div>
              </section>

              {/* Contact */}
              <section data-section="contact" className="policy-section">
                <h2>Contact Us</h2>
                <p>
                  If you have questions about this Privacy Policy or our privacy practices,
                  we're here to help:
                </p>

                <div className="contact-grid">
                  <div className="contact-card primary">
                    <h4>📧 Privacy Team</h4>
                    <p><strong>privacy@nyleads.ai</strong></p>
                    <p>For privacy-related questions and data requests</p>
                    <p className="response-time">Response within 48 hours</p>
                  </div>
                  <div className="contact-card">
                    <h4>💬 General Support</h4>
                    <p><strong>support@nyleads.ai</strong></p>
                    <p>For technical support and general inquiries</p>
                    <p className="response-time">Response within 24 hours</p>
                  </div>
                  <div className="contact-card">
                    <h4>📍 Mailing Address</h4>
                    <p><strong>NyLeads AI Legal Department</strong><br/>
                    [Your Business Address]<br/>
                    New York, NY [ZIP Code]<br/>
                    United States</p>
                  </div>
                  <div className="contact-card">
                    <h4>🏛️ Data Protection Officer</h4>
                    <p><strong>dpo@nyleads.ai</strong></p>
                    <p>For EU/UK data protection matters</p>
                    <p className="response-time">Response within 72 hours</p>
                  </div>
                </div>

                <div className="emergency-contact">
                  <h4>🚨 Security Incidents</h4>
                  <p>
                    For urgent security matters or suspected data breaches, contact us immediately at{' '}
                    <strong>security@nyleads.ai</strong> or call our 24/7 security hotline.
                  </p>
                </div>
              </section>
            </main>
          </div>
        </div>
      </div>

      {/* Footer */}
      
    </div>
  );
};

export default PrivacyPolicy;